/* eslint-disable no-console */
import { recursiveReplace } from '@principle-theorem/editor';
import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntityRecord,
  IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  DestinationEntityRecord_Key,
  SourceEntityRecord_Key,
  TimestampString,
  UUIDString,
  addDataToDestinationEntityRecord,
  getDestinationEntityRecord,
  resetModifiedDestinationEntityRecord,
  updateDestinationEntityRecordStatus,
  upsertDestinationEntityRecord,
} from '@principle-theorem/principle-db-connector';
import {
  PrimaryKey,
  WithPrimaryKey,
  WithRef,
  isArray,
  serialise,
  toMoment,
  toTimestamp,
  unserialise,
  type SerialisedData,
  asyncForAll,
} from '@principle-theorem/shared';
import { DataConnect } from 'firebase/data-connect';
import * as stringify from 'json-stable-stringify';
import { cloneDeep, first, isEmpty, omit, sortBy } from 'lodash';
import { PracticeMigration } from '../../practice-migrations';
import { IArraySorter } from '@principle-theorem/principle-core/interfaces';

export class DestinationEntityRecord {
  static async get<
    Data extends object = object,
    FailData extends object = object,
    SourceKey extends object = object,
  >(
    dataConnect: DataConnect,
    destinationRef: DestinationEntityRecord_Key
  ): Promise<
    | WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>
    | undefined
  > {
    try {
      const result = await getDestinationEntityRecord(dataConnect, {
        key: destinationRef,
      });
      const record = result.data.destinationEntityRecord;

      if (!record) {
        return;
      }

      return {
        ...record,
        migratedAt: toTimestamp(toMoment(record.migratedAt)),
        status: record.status as DestinationEntityRecordStatus,
        data: record.data as Data,
        modifiedData: record.modifiedData as Data,
        sourceRef: record.sourceRef.id,
        createdAt: toTimestamp(toMoment(record.createdAt)),
        updatedAt: toTimestamp(toMoment(record.updatedAt)),
      } as WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>;
    } catch (error) {
      console.error('DestinationEntityRecord.get', error);
      throw error;
    }
  }

  static async upsert(
    migration: WithRef<IPracticeMigration>,
    destinationRecords: (IDestinationEntityRecord &
      Partial<WithPrimaryKey<IDestinationEntityRecord>>)[],
    destinationType: string
  ): Promise<void> {
    const dataConnect = await PracticeMigration.dataConnect(migration);
    try {
      await asyncForAll(destinationRecords, async (record) =>
        upsertDestinationEntityRecord(
          dataConnect,
          this.serialise(record, destinationType)
        )
      );
    } catch (error) {
      console.error('DestinationEntityRecord.upsert', error);
      throw error;
    }
  }

  static async resetModifiedData(
    dataConnect: DataConnect,
    record: WithPrimaryKey<IDestinationEntityRecord>
  ): Promise<void> {
    try {
      await resetModifiedDestinationEntityRecord(dataConnect, {
        id: record.id,
      });
    } catch (error) {
      console.error('DestinationEntityRecord.resetModifiedData', error);
      throw error;
    }
  }

  static async setStatus(
    dataConnect: DataConnect,
    record: WithPrimaryKey<IDestinationEntityRecord>,
    status: DestinationEntityRecordStatus
  ): Promise<void> {
    try {
      await updateDestinationEntityRecordStatus(dataConnect, {
        id: record.id,
        status,
      });
    } catch (error) {
      console.error('DestinationEntityRecord.setStatus', error);
      throw error;
    }
  }

  static async updateData(
    dataConnect: DataConnect,
    recordId: PrimaryKey<IDestinationEntityRecord>,
    data: Partial<
      Pick<IDestinationEntityRecord, 'data' | 'diffData' | 'modifiedData'> &
        Pick<FailedDestinationEntityRecord, 'failData'>
    >
  ): Promise<void> {
    try {
      await addDataToDestinationEntityRecord(dataConnect, {
        id: recordId,
        ...data,
      });
    } catch (error) {
      console.error('DestinationEntityRecord.updateData', error);
      throw error;
    }
  }

  static getLatestData<T extends object = object>(
    record: WithPrimaryKey<IDestinationEntityRecord>
  ): T | undefined {
    if (!isEmpty(record.modifiedData)) {
      return record.modifiedData as T;
    }

    return record.data as T;
  }

  static hasMergeConflicts<T extends object, R extends object>(
    migrationData: T,
    principleData: R,
    additionalKeysToOmit: string[] = [],
    arraySorters: IArraySorter[] = []
  ): boolean {
    const keysToOmit = [...DEFAULT_OMIT_KEYS, ...additionalKeysToOmit];

    const serialisedMigrationData = recursiveReplace(
      cloneDeep(serialise(migrationData)),
      (item) => {
        item = omit(item, keysToOmit);
        item = sortSerialisedNestedArray(item, arraySorters);
        return item;
      }
    );
    const serialisedPrincipleData = recursiveReplace(
      cloneDeep(omit(serialise(principleData), 'ref')),
      (item) => {
        item = omit(item, keysToOmit);
        item = sortSerialisedNestedArray(item, arraySorters);
        return item;
      }
    );

    return (
      stringify(serialisedMigrationData) !== stringify(serialisedPrincipleData)
    );
  }

  static serialise(
    record: IDestinationEntityRecord,
    destinationType: string
  ): DestinationEntityUpsert {
    const serialised = serialise(record);
    const data: DestinationEntityUpsert = {
      uid: serialised.uid,
      label: serialised.label,
      destinationType,
      status: serialised.status,
      sourceRef: { id: serialised.sourceRef },
      modifiedData: serialised.modifiedData,
      diffData: record.diffData,
      migratedAt:
        serialised.status === DestinationEntityRecordStatus.Migrated
          ? serialised.migratedAt.timestampValue
          : serialise(toTimestamp()).timestampValue,
    };

    switch (serialised.status) {
      case DestinationEntityRecordStatus.Migrated:
        data.data = serialised.data;
        break;
      case DestinationEntityRecordStatus.Failed:
      case DestinationEntityRecordStatus.MissingCustomMapping:
        data.errorMessage = serialised.errorMessage;
        data.failData = serialised.failData;
        break;
      case DestinationEntityRecordStatus.MigratedWithErrors:
        data.failData = serialised.failData;
        break;
      default:
        break;
    }

    return data;
  }

  static unserialise<
    Data extends object = object,
    FailData extends object = object,
    SourceKey extends object = object,
  >(
    record: DestinationEntityResponse
  ): WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>> {
    return {
      ...record,
      sourceRef: record.sourceRef.id,
      modifiedData: (record.modifiedData as Data) ?? undefined,
      data: record.data as Data,
      failData: record.failData as FailData,
      migratedAt: toTimestamp(toMoment(record.migratedAt)),
      status: record.status as DestinationEntityRecordStatus,
      createdAt: toTimestamp(toMoment(record.createdAt)),
      updatedAt: toTimestamp(toMoment(record.updatedAt)),
    } as WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>;
  }
}

export type DestinationEntityResponse = {
  id: UUIDString;
  uid: string;
  label: string;
  destinationType: string;
  status: string;
  sourceRef: {
    id: UUIDString;
    sourceType: string;
  } & SourceEntityRecord_Key;
  errorMessage?: string | null;
  migratedAt: TimestampString;
  createdAt: TimestampString;
  updatedAt: TimestampString;
  failData?: unknown;
  data?: unknown;
  modifiedData?: unknown;
  diffData?: unknown;
} & DestinationEntityRecord_Key;

type DestinationEntityUpsert = Omit<
  DestinationEntityResponse,
  'sourceRef' | 'createdAt' | 'updatedAt' | 'id'
> & {
  id?: UUIDString;
  sourceRef: SourceEntityRecord_Key;
  failData?: object;
  data?: object;
  modifiedData?: object;
  diffData?: object;
};

export function sortNestedArray<T extends object>(
  data: T,
  arraySorters: IArraySorter[] = []
): T {
  const serialisedData = serialise(data);
  const sorted = recursiveReplace(cloneDeep(serialisedData), (item) =>
    sortSerialisedNestedArray(item, arraySorters)
  );
  return unserialise(sorted) as T;
}

export function sortSerialisedNestedArray<T extends SerialisedData<object>>(
  serialisedData: T,
  arraySorters: IArraySorter[] = []
): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const item = cloneDeep(serialisedData) as Record<string, any>;
  for (const [key] of Object.entries(item)) {
    arraySorters.map((arraySorter) => {
      if (key !== arraySorter.key) {
        return;
      }

      if (isArray(item[key])) {
        const matchesSorter = arraySorter.typeGuardFn
          ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            arraySorter.typeGuardFn(first(item[key]))
          : true;
        if (matchesSorter) {
          item[key] = sortBy(item[key], arraySorter.sortByPath);
        }
      }
    });
  }
  return item as T;
}

export const DEFAULT_OMIT_KEYS = [
  'uid',
  'uuid',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'deletedAt',
  'deleted',
  'ref',
];
